import React, { useEffect } from 'react'
import classNames from 'classnames'
import { Else, If, Then } from 'react-if'
import { Button, LinkButton } from '@/atoms/Button'
import { ChevronLeftIcon } from '@/atoms/Icons/ChevronLeftIcon'
import { PayItForwardOutlineIcon } from '@/atoms/Icons/PayItForwardOutlineIcon'
import { InternalLink } from '@/atoms/InternalLink'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { ParagraphLG, TitleMD, TitleSM } from '@/atoms/Text'
import { IconColor } from '@/atoms/utils'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { CheckedItem } from '@/molecules/CheckedItem'
import { ProjectPIFThankYou } from '@/organisms/ExpressCheckoutCompleteScreen/ProjectPIFThankYou'
import { useTellYourStoryPrompt } from '@/organisms/TellYourStoryPrompt/TellYourStoryPrompt'
import { useGuildMember } from '@/services/GuildUserService/GuildUserService'
import { Offer } from '@/services/PifService'
import { getPeopleImpacted } from '@/services/PifService/PifService'
import { formatPrice, getPriceForCurrency } from '@/services/PriceFormatter'
import { useUser } from '@/services/UserService'
import { MoneyCurrencyCode } from '@/types/codegen-federation'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { getProjectTheme } from '@/utils/project-themes'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { PaymentMethods } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PaymentMethods'
import { CheckoutLegalTermsExperiment } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/experiments/CheckoutLegalTermsExperiment'
import { GuildSubscriptionCheckoutProvider } from './GuildSubscriptionProvider'
import { PayItForwardCheckoutCta } from './PayItForwardCheckoutCta'
import { usePayItForwardState } from './usePayItForwardState'

export interface PayItForwardProjectCheckoutOfferViewProps {
  projectSlug: string
  offer: Offer
  name: string
  guildDeepLink: string
}

export const PayItForwardProjectCheckoutOfferView: React.FC<PayItForwardProjectCheckoutOfferViewProps> = ({
  name,
  projectSlug,
  offer,
  guildDeepLink,
}) => {
  const { t } = useTranslate('pif')
  const { user } = useUser()
  const track = useSafeTrack()
  const { trackCheckoutStarted } = useEcommerceEvents()
  const { isPayItForwardComplete, isSubscription } = usePayItForwardState()
  const { isGuildMember } = useGuildMember()
  const { openModal: openTellYourStoryModal, isSubmitted: isStorySubmitted } = useTellYourStoryPrompt()
  const numberOfPeople = getPeopleImpacted(offer)

  const projectTheme = getProjectTheme(projectSlug)
  const projectColors = projectTheme.lightTheme.colors
  const isFYC = slugs.forYourConsideration === projectSlug
  const getReach = () => {
    const peopleImpacted = new Intl.NumberFormat().format(numberOfPeople)
    return t('personImpacted', {
      count: numberOfPeople,
      peopleImpacted,
      defaultValue_one: 'One Person',
      defaultValue_other: '{{peopleImpacted}} People',
    })
  }
  const isShowThanksForSharing = projectSlug === slugs.hisOnlySon
  const subType = isSubscription ? t('monthly', 'Monthly') : t('oneTime', 'One Time')
  const showDeepLink = Boolean(isGuildMember || isSubscription)

  useEffect(() => {
    trackCheckoutStarted({
      currency: offer.currency as MoneyCurrencyCode,
      funnel: 'pif',
      value: getPriceForCurrency(offer.price, offer.currency),
      projectSlug,
      products: [
        {
          product_id: offer.id,
          sku: offer.slug,
          category: 'pif',
          name: offer.name,
          price: getPriceForCurrency(offer.price, offer.currency),
          currency: offer.currency as MoneyCurrencyCode,
          quantity: 1,
        },
      ],
      subscription: isSubscription,
    })
    // we only want to fire this once on page load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      className={classNames(
        'bg-core-gray-100 relative',
        isPayItForwardComplete && 'flex flex-col lg:flex-row lg:min-h-screen',
      )}
    >
      {isPayItForwardComplete && (
        <GuildSubscriptionCheckoutProvider offerId={offer?.id} isGuildMember={isGuildMember}>
          <PayItForwardCheckoutCta
            showWhiteNav={true}
            wrapperClassName={'lg:order-1 lg:mt-[64px] lg:h-auto lg:w-3/5 order-2'}
            backgroundImage={getCloudinaryImageUrl({ path: '/v1694104235/Show.png', format: 'webp' })}
            phoneImage={getCloudinaryImageUrl({ path: '/v1694107535/iPhone_14.png', format: 'webp' })}
            ctaLogo={getCloudinaryImageUrl({ path: '/v1694110171/Layer_1.png', format: 'webp' })}
            guildDeepLink={guildDeepLink}
            ctaButtonText={
              showDeepLink ? t('downloadApp', 'Download The App') : t('menuLinkJoinTheGuild', 'Join the Guild')
            }
            ctaTitleText={t('conceptVideosCastVote', 'Watch Concept Videos, Cast Your Vote')}
            ctaDescriptionText={
              showDeepLink
                ? t('angelGuildDecideConcepts', 'Start voting to decide which new concepts should move forward.')
                : t(
                    'joinAngelGuildDecideConcepts',
                    'Join the Angel Guild now and immediately start voting to decide which new concepts should move forward.',
                  )
            }
          />
        </GuildSubscriptionCheckoutProvider>
      )}
      <PaddedContainer
        className={classNames(
          'flex !max-w-[600px] flex-col py-20',
          isPayItForwardComplete && 'w-full lg:w-2/5 order-1 lg:order-2 flex justify-center',
        )}
      >
        {!isPayItForwardComplete && (
          <nav className="mb-4">
            <div onClick={() => track('Checkout Cancelled', { project: projectSlug })}>
              <InternalLink
                href={`${paths.payItForward.index}/${projectSlug}`}
                className="flex w-fit items-center text-sm font-bold"
              >
                <ChevronLeftIcon color="black" className="mr-2" />
                <Translate t={t} i18nKey="goBackAPage">
                  Back
                </Translate>
              </InternalLink>
            </div>
          </nav>
        )}
        <header className="mb-4">
          <div className="rounded-lg bg-white">
            <h1 className="photon-title-lg bg-core-gray-100 pb-4 font-bold">
              <If condition={isPayItForwardComplete}>
                <Then>
                  <Translate t={t} i18nKey="orderComplete">
                    Order Complete
                  </Translate>
                </Then>
                <Else>
                  <Translate t={t} i18nKey="orderSummary">
                    Order Summary
                  </Translate>
                </Else>
              </If>
            </h1>
            <div className="bg-core-gray-100">
              <div className="flex flex-wrap rounded-lg border border-gray-200 bg-white">
                <div className="w-full p-4">
                  <div className="flex w-full flex-wrap items-center">
                    <div className="rounded-lg bg-white pr-2">
                      <PayItForwardOutlineIcon size={21} color="gray-900" />
                    </div>
                    <div className="photon-title-sm font-bold">
                      <Translate t={t} i18nKey="payItForwardv2">
                        Pay it Forward
                      </Translate>
                    </div>
                    <div aria-label="price" className="photon-title-lg ml-auto font-bold">
                      {formatPrice(offer.price, { currency: offer.currency })}
                    </div>
                  </div>
                  <div aria-label="people-impacted" className="photon-title-xs mt-4 font-bold">
                    <div aria-label="project-name" className="photon-caption-sm mb-1 font-normal opacity-60">
                      <Translate t={t} i18nKey="show">
                        Show
                      </Translate>
                      :
                    </div>
                    {name}
                  </div>
                  <div className="flex flex-row justify-between">
                    <div aria-label="people-impacted" className="photon-title-xs mt-4 w-2/4 font-bold">
                      <div aria-label="project-name" className="photon-caption-sm mb-1 font-normal opacity-60">
                        <Translate t={t} i18nKey="subscriptionType">
                          Subscription Type
                        </Translate>
                        :
                      </div>
                      {subType}
                    </div>
                    <div aria-label="people-impacted" className="photon-title-xs mt-4 w-2/4 font-bold">
                      <div aria-label="project-name" className="photon-caption-sm mb-1 font-normal opacity-60">
                        <Translate t={t} i18nKey="reach">
                          Reach
                        </Translate>
                        :
                      </div>
                      {getReach()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {isPayItForwardComplete && (
              <div aria-label="List of Earned Perks" className="bg-core-gray-100 pt-4">
                <TitleSM weight="bold">{t('yourContribution', 'Your Contribution')}</TitleSM>
                {offer.offeredproductSet.map((perk) => (
                  <CheckedItem
                    key={perk.product.id}
                    earned
                    name={perk.name}
                    checkBackgroundColor={'core-gray-950' as IconColor}
                    checkColor={projectColors.onPrimary as IconColor}
                    textClassName="text-base lg:text-lg leading-6 md:leading-7 lg:leading-8"
                  />
                ))}
                {projectSlug === slugs.theShift && (
                  <div className="bg-core-gray-100">
                    <ProjectPIFThankYou projectName={name} />
                  </div>
                )}
              </div>
            )}
          </div>
          {!isPayItForwardComplete && <CheckoutLegalTermsExperiment />}
        </header>
        {!isPayItForwardComplete && (
          <main>
            <div className="mt-4 bg-core-gray-100">
              <>
                <h2 className="photon-title-lg bg-core-gray-100 pb-4 font-bold">
                  <Translate t={t} i18nKey="billingInformation">
                    Billing Information
                  </Translate>
                </h2>
                <div className=" rounded-lg border border-gray-200 bg-white p-4">
                  <PaymentMethods
                    offer={offer}
                    projectSlug={projectSlug}
                    isShowThanksForSharing={isShowThanksForSharing}
                  />
                </div>
              </>
            </div>
          </main>
        )}
        {isPayItForwardComplete && (
          <>
            {isShowThanksForSharing && Boolean(user) && (
              <div>
                {isStorySubmitted ? (
                  <TitleMD weight="bold" className="my-4">
                    <Translate i18nKey="thankYouForSharing" t={t}>
                      Thank You For Sharing!
                    </Translate>
                  </TitleMD>
                ) : (
                  <>
                    {projectSlug === slugs.theShift && <ProjectPIFThankYou projectName={name} />}
                    <TitleMD weight="bold" className="my-4">
                      <Translate i18nKey="shareYourStoryExclamation" t={t}>
                        Share Your Story!
                      </Translate>
                    </TitleMD>
                    <div className="flex flex-col justify-between gap-4 rounded-lg border border-gray-300 bg-white p-4 md:flex-row">
                      <ParagraphLG>
                        <Translate t={t} i18nKey="tellUsWhyYouPayItForward">
                          Tell us why you Pay it Forward by sharing a story with our community.
                        </Translate>
                      </ParagraphLG>
                      <Button variant="oxide" onClick={openTellYourStoryModal}>
                        <Translate i18nKey="shareYourStoryButton" t={t}>
                          Share Your Story
                        </Translate>
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
            <LinkButton
              aria-label="back-to-the-show-button"
              href={isFYC ? paths.watch.index : `${paths.watch.index}/${projectSlug}`}
              variant="black"
              outline
              className={classNames('mt-4 flex md:w-fit min-w-[216px] justify-center p-4 md:mx-auto lg:mx-0 w-full')}
            >
              {isFYC ? t('startWatching', 'Start Watching') : t('backToTheShow', 'Back to the show')}
            </LinkButton>
          </>
        )}
      </PaddedContainer>
    </div>
  )
}
